'use client';

import { c as _c } from "react/compiler-runtime";
import * as SliderPrimitive from '@radix-ui/react-slider';
import { TriangleAlertIcon } from 'lucide-react';
import { type ComponentProps, useCallback, useEffect, useRef, useState } from 'react';
import { cn } from '../../utils/cn';
const DEFAULT_WARNING_THRESHOLD = 85;
const BUFFER_ZONE = 2;
const KEYBOARD_TOOLTIP_TIMEOUT_MS = 1000;
const KEYBOARD_STEP_SIZE = 5;
const SHIFT_STEP_SIZE = 25;
const ARROW_KEYS = new Set(['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown']);
function getRangeBackgroundClass(disabled: boolean | undefined, isInWarningZone: boolean): string {
  if (disabled) {
    return isInWarningZone ? 'bg-destructive' : 'bg-primary/40';
  }
  if (isInWarningZone) return 'bg-destructive-foreground';
  return 'bg-primary';
}
type SliderProps = Omit<ComponentProps<typeof SliderPrimitive.Root>, 'value' | 'onValueChange'> & {
  value?: number;
  onValueChange?: (value: number) => void;
  warningThreshold?: number;
};
function Slider(t0) {
  const $ = _c(50);
  let className;
  let disabled;
  let onValueChange;
  let props;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      className,
      value: t1,
      onValueChange,
      warningThreshold: t2,
      disabled,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = disabled;
    $[3] = onValueChange;
    $[4] = props;
    $[5] = t1;
    $[6] = t2;
  } else {
    className = $[1];
    disabled = $[2];
    onValueChange = $[3];
    props = $[4];
    t1 = $[5];
    t2 = $[6];
  }
  const value = t1 === undefined ? 0 : t1;
  const warningThreshold = t2 === undefined ? DEFAULT_WARNING_THRESHOLD : t2;
  const [isInWarningZone, setIsInWarningZone] = useState(value >= warningThreshold);
  const [showKeyboardTooltip, setShowKeyboardTooltip] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const keyboardTooltipTimeoutRef = useRef(null);
  let t3;
  let t4;
  if ($[7] !== isInWarningZone || $[8] !== value || $[9] !== warningThreshold) {
    t3 = () => {
      if (isInWarningZone) {
        const exitThreshold = Math.max(0, warningThreshold - BUFFER_ZONE);
        if (value < exitThreshold) {
          setIsInWarningZone(false);
        }
      } else {
        if (value >= warningThreshold) {
          setIsInWarningZone(true);
        }
      }
    };
    t4 = [value, warningThreshold, isInWarningZone];
    $[7] = isInWarningZone;
    $[8] = value;
    $[9] = warningThreshold;
    $[10] = t3;
    $[11] = t4;
  } else {
    t3 = $[10];
    t4 = $[11];
  }
  useEffect(t3, t4);
  let t5;
  if ($[12] !== onValueChange) {
    t5 = values => {
      onValueChange?.(values[0] ?? 0);
    };
    $[12] = onValueChange;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const handleValueChange = t5;
  let t6;
  if ($[14] !== onValueChange || $[15] !== value) {
    t6 = e => {
      if (!ARROW_KEYS.has(e.key)) {
        return;
      }
      setShowKeyboardTooltip(true);
      if (keyboardTooltipTimeoutRef.current) {
        clearTimeout(keyboardTooltipTimeoutRef.current);
      }
      keyboardTooltipTimeoutRef.current = setTimeout(() => {
        setShowKeyboardTooltip(false);
      }, KEYBOARD_TOOLTIP_TIMEOUT_MS);
      e.preventDefault();
      const isIncrement = e.key === "ArrowRight" || e.key === "ArrowUp";
      const stepSize = e.shiftKey ? SHIFT_STEP_SIZE : KEYBOARD_STEP_SIZE;
      const step = isIncrement ? stepSize : -stepSize;
      const newValue = Math.max(0, Math.min(100, value + step));
      onValueChange?.(newValue);
    };
    $[14] = onValueChange;
    $[15] = value;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const handleKeyDown = t6;
  let t7;
  let t8;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => () => {
      if (keyboardTooltipTimeoutRef.current) {
        clearTimeout(keyboardTooltipTimeoutRef.current);
      }
    };
    t8 = [];
    $[17] = t7;
    $[18] = t8;
  } else {
    t7 = $[17];
    t8 = $[18];
  }
  useEffect(t7, t8);
  const showTooltip = isHovering || showKeyboardTooltip || isInWarningZone && isDragging;
  let t9;
  if ($[19] !== isInWarningZone || $[20] !== value) {
    t9 = isInWarningZone ? <div className="text-destructive-foreground flex items-center gap-1"><TriangleAlertIcon className="size-3" /><span>{value}%</span></div> : `${value}%`;
    $[19] = isInWarningZone;
    $[20] = value;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const tooltipContent = t9;
  let t10;
  if ($[22] !== className) {
    t10 = cn("relative flex w-full touch-none items-center select-none", className);
    $[22] = className;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== value) {
    t11 = [value];
    $[24] = value;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  let t13;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => setIsDragging(true);
    t13 = () => setIsDragging(false);
    $[26] = t12;
    $[27] = t13;
  } else {
    t12 = $[26];
    t13 = $[27];
  }
  let t14;
  if ($[28] !== disabled || $[29] !== isInWarningZone) {
    t14 = cn("absolute h-full transition-colors", getRangeBackgroundClass(disabled, isInWarningZone));
    $[28] = disabled;
    $[29] = isInWarningZone;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== t14) {
    t15 = <SliderPrimitive.Track className="bg-accent relative h-2 w-full grow overflow-hidden rounded-full"><SliderPrimitive.Range className={t14} /></SliderPrimitive.Track>;
    $[31] = t14;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  let t17;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = () => setIsHovering(true);
    t17 = () => setIsHovering(false);
    $[33] = t16;
    $[34] = t17;
  } else {
    t16 = $[33];
    t17 = $[34];
  }
  let t18;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = {
      touchAction: "none"
    };
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  let t19;
  if ($[36] !== showTooltip || $[37] !== tooltipContent) {
    t19 = showTooltip && <div className="bg-card absolute bottom-full left-1/2 z-50 mb-2 -translate-x-1/2 rounded-md border px-2 py-1 text-xs shadow-md">{tooltipContent}</div>;
    $[36] = showTooltip;
    $[37] = tooltipContent;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== handleKeyDown || $[40] !== t19) {
    t20 = <SliderPrimitive.Thumb className="bg-primary-foreground ring-offset-background focus-visible:ring-ring border-border relative block size-[18px] cursor-grab rounded-full border-[0.5px] shadow-md transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none active:cursor-grabbing disabled:pointer-events-none disabled:opacity-50" onMouseEnter={t16} onMouseLeave={t17} onKeyDown={handleKeyDown} style={t18}>{t19}</SliderPrimitive.Thumb>;
    $[39] = handleKeyDown;
    $[40] = t19;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] !== disabled || $[43] !== handleValueChange || $[44] !== props || $[45] !== t10 || $[46] !== t11 || $[47] !== t15 || $[48] !== t20) {
    t21 = <SliderPrimitive.Root data-slot="slider" className={t10} value={t11} onValueChange={handleValueChange} onPointerDown={t12} onPointerUp={t13} step={1} disabled={disabled} {...props}>{t15}{t20}</SliderPrimitive.Root>;
    $[42] = disabled;
    $[43] = handleValueChange;
    $[44] = props;
    $[45] = t10;
    $[46] = t11;
    $[47] = t15;
    $[48] = t20;
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  return t21;
}
export { Slider };
export type { SliderProps };